import {
  getDataFromServer,
  putDataToServer,
  deleteDataFromServerWithFullResponse,
  throwIfDeleteFailed
} from '../../../util';

class ScoringConfigModule {
  constructor(baseURL, httpHeaders) {
    this.baseURL = baseURL;
    this.httpHeaders = httpHeaders;
  }

  // FIN-13569 — `alertId` is OPTIONAL and sent only by the Spatial alert view: it
  // routes the read to the alert read scope every alert viewer holds (see the
  // `?alertId=` aliases in TmIadAaaResourcesHandler). Without it the request is
  // byte-for-byte what it always was, so Settings and every other caller are
  // unaffected; the endpoint itself ignores the parameter.
  getScoringConfig = async ({ scenarioId, alertId }) => {
    const url = `${this.baseURL}/scenarios/${scenarioId}/scoringConfig`;
    const response = alertId
      ? await getDataFromServer(url, this.httpHeaders, { alertId })
      : await getDataFromServer(url, this.httpHeaders);
    if (response && response.status && response.status !== 200) {
      throw new Error(
        response?.data?.message || 'Failed to fetch scoring config'
      );
    }
    // BE returns 200 with body=null when no row; normalize to null
    const payload = response?.data ?? response;
    if (payload === null || payload === '' || payload === 'null') {
      return null;
    }
    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload);
      } catch (_e) {
        return null;
      }
    }
    return payload ?? null;
  };

  putScoringConfig = async ({ scenarioId, config }) => {
    const url = `${this.baseURL}/scenarios/${scenarioId}/scoringConfig`;
    const response = await putDataToServer(url, this.httpHeaders, {}, config);
    if (
      response &&
      response.status &&
      response.status !== 200 &&
      response.status !== 201
    ) {
      throw new Error(
        response?.data?.message || 'Failed to save scoring config'
      );
    }
    return response;
  };

  // FIN-12858 — this guard read `response.status`, but `deleteDataFromServer` returns the
  // BODY, so it was reading a `status` field the API never sets: always undefined, so the
  // check short-circuited and every failure was reported as a success. Keep the full
  // response so the status is the HTTP one the guard always assumed.
  deleteScoringConfig = async ({ scenarioId }) => {
    const url = `${this.baseURL}/scenarios/${scenarioId}/scoringConfig`;
    return throwIfDeleteFailed(
      await deleteDataFromServerWithFullResponse(url),
      'Failed to reset scoring config'
    );
  };

  // FIN-12215: tenant/global default scoring config (FRAUD_PREVENTION scope).
  // GET/PUT only — no delete (the global default must always exist).
  // `alertId` is optional — see getScoringConfig.
  getDefaultScoringConfig = async ({ alertId } = {}) => {
    const url = `${this.baseURL}/scoringConfig/defaults`;
    const response = alertId
      ? await getDataFromServer(url, this.httpHeaders, { alertId })
      : await getDataFromServer(url, this.httpHeaders);
    if (response && response.status && response.status !== 200) {
      throw new Error(
        response?.data?.message || 'Failed to fetch default scoring config'
      );
    }
    const payload = response?.data ?? response;
    if (payload === null || payload === '' || payload === 'null') return null;
    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload);
      } catch (_e) {
        return null;
      }
    }
    return payload ?? null;
  };

  putDefaultScoringConfig = async ({ config }) => {
    const url = `${this.baseURL}/scoringConfig/defaults`;
    const response = await putDataToServer(url, this.httpHeaders, {}, config);
    if (
      response &&
      response.status &&
      response.status !== 200 &&
      response.status !== 201
    ) {
      throw new Error(
        response?.data?.message || 'Failed to save default scoring config'
      );
    }
    return response;
  };

  // FIN-12421 Item 7: tenant/global FRAML decay policy (weights, decay, multipliers,
  // load window). GET returns the active row (or `null` when unconfigured — the FRAML
  // feature is opt-in per deployment, so a null response also implies scoring is
  // disabled on this cluster). PUT upserts by policy_version_id + publishes a Kafka
  // broadcast so downstream consolidator pipelines hot-reload without a restart.
  getFramlDecayPolicy = async () => {
    const url = `${this.baseURL}/framlDecayPolicy/`;
    const response = await getDataFromServer(url, this.httpHeaders);
    if (response && response.status && response.status !== 200) {
      // 404 signals the controller isn't mounted (scoring disabled on this
      // deployment). Return null so the caller renders a "not-enabled" state
      // rather than throwing.
      if (response.status === 404) return null;
      throw new Error(
        response?.data?.message || 'Failed to fetch FRAML decay policy'
      );
    }
    const payload = response?.data ?? response;
    if (payload === null || payload === '' || payload === 'null') return null;
    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload);
      } catch (_e) {
        return null;
      }
    }
    return payload ?? null;
  };

  putFramlDecayPolicy = async ({ policy }) => {
    const url = `${this.baseURL}/framlDecayPolicy/`;
    const response = await putDataToServer(url, this.httpHeaders, {}, policy);
    if (
      response &&
      response.status &&
      response.status !== 200 &&
      response.status !== 201
    ) {
      throw new Error(
        response?.data?.message || 'Failed to save FRAML decay policy'
      );
    }
    return response;
  };
}

export default ScoringConfigModule;
